import {
  FilteredCreditApplication,
  RequiredDocumentWithStatus,
  useGetContractIframeUrlQuery,
} from '@applyonline/shared'
import { ContractIframeDto, IframeUrl } from '@applyonline/shared/types'
import { EApplicantType, EContractStatus, EFinancingProgram } from '@if/core/types'
import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import {
  applicantFullName,
  hasApplicantIncomePendingReview,
  hasMissingDocuments,
} from '../../../../../data/credit-application-selectors'
import { contractIsReceived } from '../../hooks'
import CreditStep, { CreditStepStatus } from './CreditStep'

interface ContractCreditStepProps {
  applicantType: EApplicantType
  creditApp: FilteredCreditApplication
  skipBankAccountRequiredDocuments: RequiredDocumentWithStatus[]
}
const ContractCreditStep = ({
  creditApp,
  skipBankAccountRequiredDocuments,
  applicantType,
}: ContractCreditStepProps) => {
  const { t } = useTranslation()

  const docStepDone = !hasMissingDocuments(creditApp)
  const applicant = applicantType === EApplicantType.Applicant ? creditApp.applicant : creditApp.coapplicant!

  const applicantIncomeStepDone =
    hasApplicantIncomePendingReview(creditApp, EApplicantType.Applicant, skipBankAccountRequiredDocuments) ||
    creditApp.applicant.areIncomesConfirmed

  const contractReceived = contractIsReceived(applicantType, creditApp)

  const isContractReady = creditApp.contract.status === EContractStatus.Ready

  const contractRefused = contractReceived && creditApp.contract.status === EContractStatus.Declined

  const [shouldPoll, setShouldPoll] = useState(isContractReady)

  let content = ''
  let status = isContractReady ? CreditStepStatus.RequiresAttention : CreditStepStatus.NotReady

  const {
    data: iframeDto,
    isFetching: isLoadingLink,
    failureCount,
  } = useGetContractIframeUrlQuery<ContractIframeDto>(creditApp.id, EFinancingProgram.MechanicalWarranty, shouldPoll)

  useEffect(() => {
    if (iframeDto || failureCount >= 4) setShouldPoll(false)
  }, [iframeDto, failureCount])

  const urlsDto = iframeDto?.urls?.find(
    (x: IframeUrl) => x.isForApplicant === (applicantType === EApplicantType.Applicant),
  )

  const hideLinkToDetail = contractReceived || !creditApp.cvtNumber || isLoadingLink || !urlsDto

  const applicantHasCompleted =
    iframeDto &&
    ((!urlsDto && iframeDto.completedSignatures?.length === 1) ||
      iframeDto.completedSignatures?.length > 0 ||
      iframeDto.completedSignatures?.length > 1)

  if (creditApp.contract.status === EContractStatus.NotReady && docStepDone && applicantIncomeStepDone) {
    status = CreditStepStatus.Await
    content = t('overview.noPaymentPolicyContractWait')
  } else if (isContractReady || urlsDto) {
    content = urlsDto ? t('overview.contractReady') : ''
    if (applicantHasCompleted) {
      status = CreditStepStatus.Completed
    }
  } else if (contractRefused) {
    status = CreditStepStatus.Await
    content = `${t('contract.refused')}`
  } else if (contractReceived) {
    status = CreditStepStatus.Completed
    content = `${t('contract.received')} ${t('contract.review')}`
  }

  return (
    <CreditStep
      isPending={shouldPoll}
      status={status}
      linkToDetail={urlsDto?.url ?? ''}
      hideLinkToDetail={hideLinkToDetail}
      title={t('overview.contractStep', { name: applicantFullName(applicant) })}
      content={content}
    />
  )
}

export default ContractCreditStep
